import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CeldaFolio, CeldaProducto } from "@/components/IdentificacionSolicitud";
import { estadoTono, etiquetaEstado } from "@/components/estadoSolicitud";
import { ShieldCheck, AlertCircle, ChevronsRight, Plus, Loader2, Filter, X, Building2, UserX } from "lucide-react";
import { TransitionsModal } from "@/components/TransitionsModal";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { useSeguridadAccess } from "@/hooks/useSeguridadAccess";

interface PageResp<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

export function SeguridadPage() {
  const access = useSeguridadAccess();
  const navigate = useNavigate();
  const [showSinCtxWarn, setShowSinCtxWarn] = useState(false);
  const [showConfirmCtx, setShowConfirmCtx] = useState(false);

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Seguridad"
        sectorCodigo="SEGURIDAD"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }

  const onNuevaSolicitudClick = () => {
    if (access.esInterno) {
      navigate("/sector/seguridad/solicitudes/nueva");
      return;
    }
    const sel = window.sessionStorage.getItem("sicen.contexto.titularId");
    if (!sel) setShowSinCtxWarn(true);
    else setShowConfirmCtx(true);
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-fg">Seguridad</h1>
        <p className="text-sm text-fg-muted">
          Solicitudes de certificación del sector Seguridad.
        </p>
      </header>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg border bg-surface p-1 text-sm">
          <span className="flex items-center gap-2 rounded bg-primary px-3 py-2 text-white shadow">
            <ShieldCheck className="h-4 w-4" />
            <span>Solicitudes</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onNuevaSolicitudClick}
          className="inline-flex items-center gap-1 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> Nueva solicitud
        </button>
      </div>

      <SolicitudesTab />

      {showSinCtxWarn && <SinContextoModal onClose={() => setShowSinCtxWarn(false)} />}
      {showConfirmCtx && (
        <ConfirmarContextoModal
          titularRazonSocial={access.titularRazonSocial ?? ""}
          onCancel={() => setShowConfirmCtx(false)}
          onConfirm={() => {
            setShowConfirmCtx(false);
            navigate("/sector/seguridad/solicitudes/nueva");
          }}
        />
      )}
    </div>
  );
}

function SinContextoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <AlertCircle className="h-5 w-5 text-estado-warn" />
          <h2 className="text-base font-semibold text-fg">Selecciona una empresa</h2>
        </div>
        <div className="px-5 py-4 text-sm text-fg-muted">
          <p>Antes de crear una nueva solicitud necesitas elegir la empresa para la que vas a trabajar.</p>
          <p className="mt-2 text-xs text-fg-muted">
            Usa el selector de empresa en la barra superior (junto a tu nombre) y vuelve a intentarlo.
          </p>
        </div>
        <div className="flex justify-end border-t bg-surface-2 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmarContextoModal({
  titularRazonSocial,
  onCancel,
  onConfirm,
}: {
  titularRazonSocial: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <Building2 className="h-5 w-5 text-primary-strong" />
          <h2 className="text-base font-semibold text-fg">Nueva solicitud de Seguridad</h2>
        </div>
        <div className="space-y-2 px-5 py-4 text-sm text-fg-muted">
          <p>Estás a punto de crear una nueva solicitud para:</p>
          <div className="rounded border border-estado-info/25 bg-estado-info-bg px-3 py-2 font-medium text-estado-info">
            {titularRazonSocial || "—"}
          </div>
          <p className="text-xs text-fg-muted">
            Si necesitas trabajar con otra empresa, cancela y cámbiala en el selector de empresa de la barra superior.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button type="button" onClick={onCancel} className="rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2">
            Cancelar
          </button>
          <button type="button" onClick={onConfirm} className="rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover">
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

type FiltroSolicitudes = "todas" | "en-proceso" | "sin-asignar";

interface SolicitudRow {
  id: string;
  folio: string;
  titularRazonSocial: string;
  tramitadorRazonSocial: string | null;
  servicioCodigo: string | null;
  productoNombre: string;
  marca: string;
  modelos: string | null;
  numReferencia: string | null;
  instanciaId: string | null;
  estadoCodigo: string | null;
  cerrada: boolean;
  /** Responsable de la etapa actual (RF-03); solo llega para internos. */
  responsableId: string | null;
  responsableNombre: string | null;
}

function SolicitudesTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const access = useSeguridadAccess();
  const esInterno = access.esInterno;
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroParam = searchParams.get("filtro");
  const filtro: FiltroSolicitudes =
    filtroParam === "en-proceso" ? "en-proceso"
    : filtroParam === "sin-asignar" && esInterno ? "sin-asignar"
    : "todas";
  const [avanzar, setAvanzar] = useState<SolicitudRow | null>(null);
  const q = useQuery({
    queryKey: ["solicitudes-seguridad"],
    queryFn: async () =>
      (await api.get<PageResp<SolicitudRow>>("/api/sector/seguridad/solicitudes?size=50&sort=fechaCreacion,desc")).data,
  });

  const setFiltro = (f: FiltroSolicitudes) => {
    const next = new URLSearchParams(searchParams);
    if (f === "todas") next.delete("filtro");
    else next.set("filtro", f);
    setSearchParams(next, { replace: true });
  };

  const filas = useMemo(() => {
    if (!q.data) return [];
    if (filtro === "en-proceso") return q.data.content.filter((r) => !r.cerrada);
    if (filtro === "sin-asignar") return q.data.content.filter((r) => !r.cerrada && !r.responsableId);
    return q.data.content;
  }, [q.data, filtro]);

  if (q.isLoading) {
    return <div className="rounded-lg border bg-surface p-6 text-sm text-fg-subtle">Cargando…</div>;
  }
  if (q.error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
        <AlertCircle className="mt-0.5 h-4 w-4" />
        <div>Error cargando solicitudes.</div>
      </div>
    );
  }
  if (!q.data) return null;
  const data = q.data;
  const totalVisible = filas.length;
  const totalGlobal = data.totalElements;
  const enProcesoTotal = data.content.filter((r) => !r.cerrada).length;

  return (
    <>
      <div className="rounded-lg border bg-surface shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wide text-fg-subtle">
              {filtro === "en-proceso" ? `${totalVisible} en proceso` : `${totalGlobal} solicitudes`}
            </span>
            {filtro === "en-proceso" && totalGlobal > totalVisible && (
              <span className="text-[11px] text-fg-faint">de {totalGlobal} totales</span>
            )}
          </div>
          <div className="flex items-center gap-1 rounded border bg-surface-2 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setFiltro("todas")}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${
                filtro === "todas" ? "bg-surface shadow-sm font-medium text-fg" : "text-fg-muted hover:text-fg"
              }`}
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => setFiltro("en-proceso")}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${
                filtro === "en-proceso" ? "bg-surface shadow-sm font-medium text-estado-warn" : "text-fg-muted hover:text-fg"
              }`}
            >
              <Filter className="h-3 w-3" />
              En proceso
              {enProcesoTotal > 0 && (
                <span className="ml-0.5 inline-block min-w-[18px] rounded-full bg-estado-warn-bg px-1 text-center text-[10px] font-bold text-estado-warn">
                  {enProcesoTotal}
                </span>
              )}
            </button>
            {esInterno && (
              <button
                type="button"
                onClick={() => setFiltro("sin-asignar")}
                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 transition ${
                  filtro === "sin-asignar" ? "bg-surface shadow-sm font-medium text-estado-warn" : "text-fg-muted hover:text-fg"
                }`}
              >
                <UserX className="h-3 w-3" />
                Sin asignar
              </button>
            )}
          </div>
        </div>
        {filas.length === 0 ? (
          <div className="p-6 text-center text-sm text-fg-subtle">
            {filtro !== "todas" ? (
              <>
                {filtro === "sin-asignar" ? "No hay solicitudes sin asignar." : "No hay solicitudes en proceso."}{" "}
                <button
                  type="button"
                  onClick={() => setFiltro("todas")}
                  className="inline-flex items-center gap-1 text-primary-strong hover:underline"
                >
                  <X className="h-3 w-3" /> Ver todas
                </button>
              </>
            ) : (
              "No hay solicitudes registradas."
            )}
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-surface-2">
              <tr>
                {["Folio", "Titular", "Tramitador", "Trámite", "Producto", "Estado",
                  ...(esInterno ? ["Responsable"] : []), "Acciones"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y bg-surface">
              {filas.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer transition hover:bg-surface-2"
                  onClick={() => navigate(`/sector/seguridad/solicitudes/${r.id}`)}
                >
                  <td className="px-4 py-2 align-top">
                    <CeldaFolio folio={r.folio} numReferencia={r.numReferencia} />
                  </td>
                  <td className="px-4 py-2 align-top font-medium">{r.titularRazonSocial}</td>
                  <td className="px-4 py-2 align-top text-xs">{r.tramitadorRazonSocial ?? "—"}</td>
                  <td className="px-4 py-2 align-top text-xs">{r.servicioCodigo ?? "—"}</td>
                  <td className="px-4 py-2 align-top">
                    <CeldaProducto producto={r.productoNombre} marca={r.marca} modelos={r.modelos} />
                  </td>
                  <td className="px-4 py-2 align-top">
                    <Badge tone={estadoTono(r.estadoCodigo, r.cerrada)}>{etiquetaEstado(r.estadoCodigo)}</Badge>
                  </td>
                  {esInterno && (
                    <td className="px-4 py-2 align-top text-xs">
                      {r.responsableNombre ? (
                        r.responsableNombre
                      ) : r.cerrada ? (
                        <span className="text-fg-faint">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-medium text-estado-warn">
                          <UserX className="h-3 w-3" /> Sin asignar
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2 align-top">
                    {r.instanciaId && !r.cerrada ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAvanzar(r);
                        }}
                        className="inline-flex items-center gap-1 rounded border border-estado-info/25 px-2 py-1 text-xs text-estado-info transition hover:bg-estado-info-bg"
                      >
                        <ChevronsRight className="h-3 w-3" />
                        Avanzar
                      </button>
                    ) : (
                      <span className="text-xs text-fg-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {avanzar?.instanciaId && (
        <TransitionsModal
          open={!!avanzar}
          solicitudId={avanzar.id}
          instanciaId={avanzar.instanciaId}
          folio={avanzar.folio}
          sector="seguridad"
          onClose={() => setAvanzar(null)}
          onDone={async () => {
            setAvanzar(null);
            await qc.invalidateQueries({ queryKey: ["solicitudes-seguridad"] });
          }}
        />
      )}
    </>
  );
}

function Badge({ tone, children }: { tone: "ok" | "info" | "warn" | "err"; children: React.ReactNode }) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
    err: "bg-estado-err-bg text-estado-err",
  }[tone];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}
